import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import deleteSiteBillingConfigEntry from "../http/deleteSiteBillingConfig";
import getSiteBillingConfig, {
  SiteBillingConfigResponse,
} from "../http/getSiteBillingConfig";
import {
  CreateBillingConfigEntryRequest,
  postSiteBillingConfigEntry,
  putSiteBillingConfigEntry,
  UpdateBillingConfigEntryRequest,
} from "../http/putSiteBillingConfig";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

export const useSiteBillingConfig = (siteId: string | undefined) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();

  const query = useQuery<SiteBillingConfigResponse>(
    ["siteBillingConfig", siteId],
    async () => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      return getSiteBillingConfig(token, siteId!);
    },
    {
      enabled: !!siteId,
      staleTime: 60_000,
    }
  );

  const createEntry = useMutation(
    async (request: CreateBillingConfigEntryRequest) => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      return postSiteBillingConfigEntry(token, siteId!, request);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["siteBillingConfig", siteId]);
      },
    }
  );

  const updateEntry = useMutation(
    async ({
      entryId,
      request,
    }: {
      entryId: number;
      request: UpdateBillingConfigEntryRequest;
    }) => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      return putSiteBillingConfigEntry(token, siteId!, entryId, request);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["siteBillingConfig", siteId]);
      },
    }
  );

  const deleteEntry = useMutation(
    async (entryId: number) => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      return deleteSiteBillingConfigEntry(token, siteId!, entryId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["siteBillingConfig", siteId]);
      },
    }
  );

  return {
    billingConfig: query,
    createEntry,
    updateEntry,
    deleteEntry,
  };
};
